/**
 * 05 缴费指引 `/payment/:registrationId`
 * -----------------------------------------------------------------------------
 * 版面按设计稿「学员端 · 缴费指引」：
 *   应缴金额（渐变）→ 收款账户 → 转账备注码（琥珀强调）→ 转账后会发生什么 → 底部两键
 *
 * - 显著展示**备注码**并支持一键复制（转账备注必填，否则无法自动核销）
 * - 展示**收款账户**（`GET /api/h5/payment/{id}/info` 的 payeeInfo；未配置时回落
 *   「联系客服」占位文案，绝不伪造账户 —— 配置入口见 `payment.payee-info.service.ts`）
 * - **不提供任何在线支付入口**（一期走线下转账 + 备注码自动核销）；
 *   设计稿的「上传转账凭证」一期没有对应端点，故不落按钮（不扩范围）
 */

import { useQuery } from '@tanstack/react-query';
import { Toast } from 'antd-mobile';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchPaymentInfo } from '../api/h5';
import { useRegistration } from '../api/hooks';
import { formatCentsText } from '../components/FormRenderer/logic';
import {
  Button,
  Card,
  ErrorBlock,
  KV,
  LoadingBlock,
  Notice,
  PageShell,
} from '../components/layout';
import { copyText } from '../lib/clipboard';

/** 截止时间展示（本地时区，分钟精度） */
function formatDueAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getMonth() + 1} 月 ${date.getDate()} 日 ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const FLOW_NOTES = [
  '通过网银或手机银行向上述账户转账，金额与备注码需完全一致',
  '系统按备注码自动匹配核销，约 1~2 个工作日',
  '核销成功后将推送「报名成功」通知，名额随即锁定',
] as const;

export default function PaymentGuide() {
  const navigate = useNavigate();
  const { registrationId } = useParams<{ registrationId: string }>();
  const detail = useRegistration(registrationId);
  // 收款信息 + 截止时间（端点仅在 PENDING_PAYMENT 可用；其它状态静默降级为占位文案）
  const payInfo = useQuery({
    queryKey: ['h5', 'payment-info', registrationId],
    queryFn: () => fetchPaymentInfo(registrationId as string),
    enabled: Boolean(registrationId) && detail.data?.status === 'PENDING_PAYMENT',
    retry: false,
  });

  const onCopy = useCallback(async (text: string, label: string) => {
    const ok = await copyText(text);
    Toast.show({
      icon: ok ? 'success' : 'fail',
      content: ok ? `${label}已复制` : '复制失败，请手动输入',
    });
  }, []);

  if (detail.isPending) {
    return (
      <PageShell title="缴费指引" back>
        <LoadingBlock text="正在加载缴费信息" />
      </PageShell>
    );
  }
  if (detail.error || !detail.data) {
    return (
      <PageShell title="缴费指引" back>
        <ErrorBlock
          message={detail.error instanceof Error ? detail.error.message : '报名单不存在'}
          onRetry={() => {
            void detail.refetch();
          }}
        />
      </PageShell>
    );
  }

  const data = detail.data;
  const payee = payInfo.data?.payeeInfo;

  return (
    <PageShell
      title="缴费指引"
      back
      layout="detail"
      footerRows={2}
      footer={
        <>
          {/* 设计稿页脚：主操作在上、次操作在下，两排 46 全圆按钮 */}
          <Button
            variant="primary"
            block
            className="btn--bar"
            onClick={() => navigate(`/registration/${data.id}`)}
          >
            查看报名详情
          </Button>
          <Button
            variant="ghost"
            block
            className="btn--bar"
            onClick={() => navigate('/help/service')}
          >
            联系客服协助
          </Button>
        </>
      }
    >
      <div className="amount-card">
        <span className="amount-card__label">应缴金额</span>
        <span className="amount-card__value mono-num">{formatCentsText(data.payableCents)}</span>
        <span className="amount-card__desc">
          {/* 逾期不自动作废（R-07：转人工跟进），不写「释放名额」这类空头承诺 */}
          {payInfo.data?.dueAt
            ? `请在 ${formatDueAt(payInfo.data.dueAt)} 前完成转账；如已转账或逾期未到账，请联系客服`
            : '请在缴费截止时间前完成转账；如已转账或逾期未到账，请联系客服'}
        </span>
      </div>

      <Card title="收款账户" tight>
        {payee ? (
          <>
            <KV label="收款户名" value={payee.accountName} />
            <KV label="开户银行" value={payee.bankName} />
            <KV
              label="银行账号"
              value={
                <span className="value-with-chip">
                  <span className="mono-num">{payee.accountNo}</span>
                  <button
                    type="button"
                    className="inline-copy"
                    onClick={() => void onCopy(payee.accountNo, '账号')}
                  >
                    复制
                  </button>
                </span>
              }
            />
          </>
        ) : (
          <p className="card__desc">
            收款账户由客服在收款前提供，请勿向任何个人账户转账。可点下方「联系客服」获取对公账户信息。
          </p>
        )}
        {payee?.remark ? <p className="card__desc payee-remark">注意事项：{payee.remark}</p> : null}
        <KV label="报名单号" value={<span className="mono-num">{data.registrationNo}</span>} />
      </Card>

      <div className="remark-card">
        <div className="remark-card__title">转账备注码（务必填写）</div>
        <div className="remark-card__value">
          <span className="remark-card__code mono-num">{data.paymentCode ?? '生成中'}</span>
          {data.paymentCode ? (
            <button
              type="button"
              className="remark-card__copy"
              onClick={() => void onCopy(data.paymentCode as string, '备注码')}
            >
              复制
            </button>
          ) : null}
        </div>
        <div className="remark-card__hint">
          填写备注码后系统可自动匹配核销；未填写或金额不符将转人工处理，到账确认会明显变慢。
        </div>
      </div>

      <Card title="转账后会发生什么" tight>
        <ul className="bullet-list bullet-list--brand">
          {FLOW_NOTES.map((text) => (
            <li key={text} className="bullet-list__item">
              <span className="bullet-list__dot" />
              <span>{text}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Notice tone="warning">教材运费到付，系统不结算运费；请勿通过任何站外代缴渠道缴费。</Notice>

      <Notice tone="info">
        转账完成后可回到「报名详情」查看状态变化，核销成功会同时推送公众号消息。
      </Notice>
    </PageShell>
  );
}
